import React, { useState, useMemo } from 'react'
import cn, { Argument as ClassName } from 'classnames'

import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'
import { IChargePoint } from '../../common/hooks/useChargePoint'
import { FirmwareGeneralModel, useChargePointUpdateFirmware2Mutation } from '../../common/redux/api/web-api'
import BootstrapModalLayout from '../../ui/BootstrapModalLayout'
import Text from '../../ui/Text'
import useKeyboardHandle from '../../common/hooks/useKeyboardHandle'
import { Combobox } from '../../common/components/combobox'
import useText from '../../common/hooks/useText'

export type UpgradeFirmwareVersionModalProps = {
    onClose: () => void
    chargePoint: IChargePoint
    availableFirmwares: FirmwareGeneralModel[] | undefined
    firmwareLoading: boolean
    getFirmwareQuerySuccess: boolean
    className?: ClassName
}

export function UpgradeFirmwareVersionModal({
    onClose,
    className,
    chargePoint,
    firmwareLoading,
    getFirmwareQuerySuccess,
    availableFirmwares,
}: UpgradeFirmwareVersionModalProps): React.ReactElement {
    const text = useText()
    const [selectedFirmware, setSelectedFirmware] = useState<number>()

    const firmwareOptions = useMemo(
        () =>
            availableFirmwares?.map(
                (option) => ({ value: option?.id, label: option?.firmwareVersion }) as { value: number; label: string }
            ),
        [availableFirmwares]
    )
    const [updateFirmware] = useChargePointUpdateFirmware2Mutation()

    const upgrade = async () => {
        if (!selectedFirmware) return
        try {
            await updateFirmware({
                chargePointId: chargePoint.id,
                updateChargerFirmwareModel: { firmwareId: selectedFirmware },
            }).unwrap()
            notifySuccess(
                text('msgFWUpdateScheduleInfo', {
                    name: chargePoint.id,
                })
            )
        } catch (_) {
            notifyError('Could not update firmware')
        }

        onClose()
    }

    useKeyboardHandle({
        onEnterPress: () => {
            if (selectedFirmware) upgrade()
        },
        onEscapePress: onClose,
    })

    return (
        <BootstrapModalLayout
            title={text('lblUpgradeVersionModal')}
            actions={[
                {
                    label: text('btnCancel'),
                    callback: onClose,
                    btn: 'btn-secondary',
                },
                {
                    label: text('btnOK'),
                    callback: upgrade,
                    disabled: !selectedFirmware || firmwareLoading,
                },
            ]}
            size="sm"
            className={cn(className)}
            style={{ overflow: 'visible' }}
        >
            {firmwareLoading && (
                <p data-testid="loading-message">
                    <Text code="msgLoadingAvailableFirmwareVersions" />
                </p>
            )}
            {getFirmwareQuerySuccess && (
                <>
                    <Combobox
                        id="firmwareOptions"
                        data-testid="firmwareOptions-select"
                        options={firmwareOptions}
                        onChange={(option) => {
                            setSelectedFirmware(option.value)
                        }}
                        value={selectedFirmware}
                        enableDefaultOption={false}
                    />
                </>
            )}
        </BootstrapModalLayout>
    )
}
